import { Button } from "@/components/ui/button";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDebounce } from "hooks/lib-ui";
import { useCityMap } from "../../hooks/use-city-map";
import { ServiceAccountFilterParams, useGetServiceAccounts } from "../../hooks/use-service-accounts";
import { ServiceAccount, ServiceAccountWithCities } from "../../service-accounts.type";
import { PaginationParams } from "../../users.type";
import { ESortOrder } from "../../users.const";
import { UsersSkeleton } from "../users-skeleton";
import { DataTable } from "../users-table/data-table";
import { ApiKeyDialog } from "./api-key-dialog";
import { ServiceAccountModal } from "./service-account-modal";
import { serviceAccountColumns } from "./service-accounts-column";

interface ServiceAccountsTableProps {
  currentPage: PaginationParams;
  setCurrentPage: (page: number) => void;
}

export const ServiceAccountsTable = ({ currentPage, setCurrentPage }: ServiceAccountsTableProps) => {
  const [searchString, setSearchString] = useState("");
  const debouncedSearch = useDebounce(searchString, 300);
  const prevDebouncedRef = useRef(debouncedSearch);
  const [sorting, setSorting] = useState<SortingState>([]);
  const prevSortRef = useRef(sorting);

  const [selectedAccount, setSelectedAccount] = useState<ServiceAccountWithCities | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [apiKeyToShow, setApiKeyToShow] = useState<string | null>(null);

  const { cityMap, loading: citiesLoading } = useCityMap();

  const filter: ServiceAccountFilterParams = useMemo(
    () => ({
      pageIndex: currentPage.pageIndex ?? 1,
      pageSize: currentPage.pageSize ?? 10,
      searchString: debouncedSearch || undefined,
      ...(sorting[0]
        ? { sortField: sorting[0].id, sortOrder: sorting[0].desc ? ESortOrder.desc : ESortOrder.asc }
        : {}),
    }),
    [currentPage.pageIndex, currentPage.pageSize, debouncedSearch, sorting],
  );

  const { serviceAccounts, loading: accountsLoading } = useGetServiceAccounts(filter, true);

  const loading = accountsLoading || citiesLoading;

  useEffect(() => {
    if (prevDebouncedRef.current !== debouncedSearch) {
      prevDebouncedRef.current = debouncedSearch;
      setCurrentPage(1);
    }
  }, [debouncedSearch, setCurrentPage]);

  useEffect(() => {
    if (prevSortRef.current !== sorting) {
      prevSortRef.current = sorting;
      setCurrentPage(1);
    }
  }, [sorting, setCurrentPage]);

  const mappedAccounts: ServiceAccountWithCities[] = useMemo(() => {
    if (!serviceAccounts?.results) return [];
    return serviceAccounts.results.map((account: ServiceAccount) => ({
      ...account,
      citiesDisplay: account.cities
        ?.map((id: string) => cityMap.get(id) ?? null)
        .filter((city): city is string => city !== null),
    }));
  }, [serviceAccounts, cityMap]);

  const handleEdit = useCallback((account: ServiceAccountWithCities) => {
    setSelectedAccount(account);
  }, []);

  const handleApiKeyGenerated = useCallback((apiKey: string) => {
    setApiKeyToShow(apiKey);
  }, []);

  const columns = useMemo(
    () =>
      serviceAccountColumns({
        onEdit: handleEdit,
        onApiKeyGenerated: handleApiKeyGenerated,
      }),
    [handleEdit, handleApiKeyGenerated],
  );

  if (loading && !serviceAccounts) {
    return (
      <div className="p-4 border rounded-md mt-4">
        <UsersSkeleton />
      </div>
    );
  }

  return (
    <>
      <div className="w-auto flex justify-end absolute right-4 top-6">
        <Button
          onClick={() => setShowCreateModal(true)}
          variant="outline"
          className="text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-9"
        >
          <span className="hidden lg:inline">Créer</span>
        </Button>
      </div>

      <DataTable
        columns={columns as ColumnDef<ServiceAccountWithCities>[]}
        data={mappedAccounts}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={serviceAccounts?.totalPages ?? 0}
        searchValue={searchString}
        onSearchChange={setSearchString}
        searchPlaceholder="Rechercher par nom ou email..."
        sorting={sorting}
        onSortingChange={setSorting}
      />

      {showCreateModal && (
        <ServiceAccountModal onClose={() => setShowCreateModal(false)} onApiKeyGenerated={handleApiKeyGenerated} />
      )}

      {selectedAccount && (
        <ServiceAccountModal serviceAccount={selectedAccount} onClose={() => setSelectedAccount(null)} />
      )}

      <ApiKeyDialog apiKey={apiKeyToShow} open={!!apiKeyToShow} onClose={() => setApiKeyToShow(null)} />
    </>
  );
};
